import React, { useEffect, useState } from "react";
import { api, useAuth } from "@/contexts/AuthContext";
import Navbar from "@/components/Navbar";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Link, useSearchParams } from "react-router-dom";
import TaskDetailDrawer from "@/components/TaskDetailDrawer";
import { CheckSquare, Plus, Trash2, AlertTriangle, CalendarClock, Clock, ListTodo, CheckCircle2 } from "lucide-react";

const BUCKETS = [
  { key: "overdue", label: "Overdue", icon: AlertTriangle, cls: "text-red-700 bg-red-50 border-red-200" },
  { key: "today", label: "Due today", icon: CalendarClock, cls: "text-amber-700 bg-amber-50 border-amber-200" },
  { key: "coming_due", label: "Coming due (7 days)", icon: Clock, cls: "text-[#0B132B] bg-[#D4AF37]/10 border-[#D4AF37]/40" },
  { key: "later", label: "Later / no date", icon: ListTodo, cls: "text-[#6C757D] bg-[#F8F9FA] border-[#E9ECEF]" },
  { key: "done", label: "Completed", icon: CheckCircle2, cls: "text-emerald-700 bg-emerald-50 border-emerald-200" },
];

function TaskRow({ t, me, onToggle, onDelete, onOpen }) {
  return (
    <li className={`flex items-start gap-3 p-3 border-b border-[#F1F3F5] last:border-0 ${t.done ? "opacity-60" : ""}`} data-testid={`task-row-${t.id}`}>
      <input type="checkbox" checked={t.done} onChange={() => onToggle(t)} className="mt-1 h-4 w-4 accent-[#0B132B] cursor-pointer" data-testid={`task-toggle-${t.id}`} />
      <div className="flex-1 min-w-0 cursor-pointer" onClick={() => onOpen(t)} data-testid={`task-open-${t.id}`}>
        <div className={`text-sm font-semibold text-[#0B132B] hover:underline ${t.done ? "line-through" : ""}`}>{t.title}
          {(t.priority === "high" || t.priority === "urgent") && !t.done && <span className="ml-2 text-[9px] uppercase tracking-[0.14em] font-bold text-red-600">{t.priority}</span>}
          {t.status === "in_progress" && !t.done && <span className="ml-2 text-[9px] uppercase tracking-[0.14em] font-bold text-[#1C7ED6]">In progress</span>}
        </div>
        <div className="text-[11px] text-[#6C757D] flex flex-wrap gap-x-3">
          {t.due_date && <span>Due {t.due_date}{t.due_time ? ` ${t.due_time}` : ""}</span>}
          <span>{(t.assignee_names || [t.assigned_to_name]).map((n, i) => ((t.assignees || [])[i] === me ? "Me" : n)).join(", ")}</span>
          {t.created_by !== t.assigned_to && <span>from {t.created_by_name}</span>}
          {t.onboarding_id ? <Link to={`/onboarding/${t.onboarding_id}`} onClick={(e) => e.stopPropagation()} className="underline text-[#0B132B]">{t.dealer_name || "Dealer record"}</Link>
            : t.dealer_id ? <Link to={`/dealers/${t.dealer_id}`} onClick={(e) => e.stopPropagation()} className="underline text-[#0B132B]">{t.dealer_name}</Link> : null}
          {(t.comments || []).length > 0 && <span>{t.comments.length} comment{t.comments.length > 1 ? "s" : ""}</span>}
          {(t.attachments || []).length > 0 && <span>{t.attachments.length} file{t.attachments.length > 1 ? "s" : ""}</span>}
          {t.done && t.completed_at && <span>done {new Date(t.completed_at).toLocaleDateString()}</span>}
        </div>
        {t.notes && <div className="text-xs text-[#6C757D] mt-0.5 line-clamp-2">{t.notes}</div>}
      </div>
      <button type="button" onClick={() => onDelete(t)} className="text-[#6C757D] hover:text-red-600" data-testid={`task-delete-${t.id}`}><Trash2 size={13} /></button>
    </li>
  );
}

export default function Tasks() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const openId = params.get("task");
  const [data, setData] = useState(null);
  const [assignees, setAssignees] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [showDone, setShowDone] = useState(false);
  const [f, setF] = useState({ title: "", due_date: "", assigned_to: "", priority: "normal", notes: "", dealer_id: "" });
  const titleRef = React.useRef(null);
  useEffect(() => { if (params.get("new") === "1") setTimeout(() => titleRef.current?.focus(), 200); }, [params]);
  const load = () => api.get("/tasks", { params: { include_done: showDone } }).then(({ data }) => setData(data)).catch(() => toast.error("Could not load tasks"));
  useEffect(() => { load(); }, [showDone]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    api.get("/tasks/assignees").then(({ data }) => setAssignees(data || [])).catch(() => {});
    api.get("/dealers").then(({ data }) => setDealers((Array.isArray(data) ? data : data?.items || []).map((d) => ({ id: d.id, name: d.dealer_name })))).catch(() => {});
  }, []);

  const add = async () => {
    if (!f.title.trim()) return;
    try {
      await api.post("/tasks", { ...f, due_date: f.due_date || null, assigned_to: f.assigned_to || null, dealer_id: f.dealer_id || null });
      setF({ title: "", due_date: "", assigned_to: "", priority: "normal", notes: "", dealer_id: "" }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add task"); }
  };
  const toggle = async (t) => { try { await api.patch(`/tasks/${t.id}`, { done: !t.done }); load(); } catch (e) { toast.error("Update failed"); } };
  const del = async (t) => { if (!window.confirm("Delete this task?")) return; try { await api.delete(`/tasks/${t.id}`); load(); } catch (e) { toast.error("Delete failed"); } };
  const openTask = (t) => { params.set("task", t.id); setParams(params); };
  const closeTask = () => { params.delete("task"); setParams(params); };

  const tasks = data?.tasks || [];
  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8" data-testid="tasks-page">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#D4AF37]">My team</div>
            <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-[#0B132B] flex items-center gap-2"><CheckSquare size={28} /> Tasks</h1>
            <p className="text-sm text-[#6C757D] mt-1">Your tasks and those of anyone who reports to you. Overdue first, then due today, then coming due.</p>
          </div>
          <label className="flex items-center gap-2 text-xs text-[#6C757D] cursor-pointer">
            <input type="checkbox" checked={showDone} onChange={(e) => setShowDone(e.target.checked)} className="accent-[#0B132B]" data-testid="tasks-show-done" /> Show completed
          </label>
        </div>

        <Card className="p-4 mb-6 border-[#0B132B]/15 rounded-sm bg-white" data-testid="task-add-card">
          <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr_1fr_1fr_0.7fr_auto] gap-2 items-end">
            <div><div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#6C757D] mb-1">Task</div>
              <Input ref={titleRef} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="e.g. Call Pacific Coast about ACH form" className="h-10 rounded-sm" data-testid="task-title-input" /></div>
            <div><div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#6C757D] mb-1">Due</div>
              <Input type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} className="h-10 rounded-sm" data-testid="task-due-input" /></div>
            <div><div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#6C757D] mb-1">Dealer</div>
              <select value={f.dealer_id} onChange={(e) => setF({ ...f, dealer_id: e.target.value })} className="h-10 w-full rounded-sm border border-[#0B132B]/15 bg-white px-2 text-sm" data-testid="task-dealer-select">
                <option value="">— none —</option>{dealers.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select></div>
            <div><div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#6C757D] mb-1">Assign to</div>
              <select value={f.assigned_to} onChange={(e) => setF({ ...f, assigned_to: e.target.value })} className="h-10 w-full rounded-sm border border-[#0B132B]/15 bg-white px-2 text-sm" data-testid="task-assignee-select">
                <option value="">Me</option>{assignees.filter((a) => a.id !== user?.id).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select></div>
            <div><div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#6C757D] mb-1">Priority</div>
              <select value={f.priority} onChange={(e) => setF({ ...f, priority: e.target.value })} className="h-10 w-full rounded-sm border border-[#0B132B]/15 bg-white px-2 text-sm" data-testid="task-priority-select">
                <option value="normal">Normal</option><option value="high">High</option><option value="urgent">Urgent</option><option value="low">Low</option>
              </select></div>
            <Button onClick={add} disabled={!f.title.trim()} className="h-10 rounded-sm bg-[#0B132B] hover:bg-[#1C2541] text-white" data-testid="task-add-btn"><Plus size={14} className="mr-1" /> Add</Button>
          </div>
        </Card>

        {data && (
          <div className="space-y-4">
            {BUCKETS.filter((b) => b.key !== "done" || showDone).map((b) => {
              const rows = tasks.filter((t) => t.bucket === b.key);
              const Icon = b.icon;
              return (
                <Card key={b.key} className="rounded-sm border-[#E9ECEF] bg-white overflow-hidden" data-testid={`task-bucket-${b.key}`}>
                  <div className={`flex items-center gap-2 px-4 py-2 border-b text-[11px] uppercase tracking-[0.18em] font-bold ${b.cls}`}>
                    <Icon size={13} /> {b.label} <span className="ml-auto font-mono" data-testid={`task-bucket-count-${b.key}`}>{rows.length}</span>
                  </div>
                  {rows.length === 0 ? <div className="px-4 py-3 text-xs text-[#6C757D] italic">Nothing here.</div>
                    : <ul>{rows.map((t) => <TaskRow key={t.id} t={t} me={user?.id} onToggle={toggle} onDelete={del} onOpen={openTask} />)}</ul>}
                </Card>
              );
            })}
          </div>
        )}
        <TaskDetailDrawer taskId={openId} onClose={closeTask} onChanged={load} />
      </main>
    </div>
  );
}
